import Vue from 'vue'
import Bugsnag from '@bugsnag/js'
import BugsnagPluginVue from '@bugsnag/plugin-vue'
import LogRocket from 'logrocket'
import Clarity from '@microsoft/clarity'
import Posthog from 'posthog-js'
import isDesktopApp from '@/desktop-app/isDesktopApp'
import syncBugsnagReporting, {syncBugsnagUser} from '@/desktop-app/bugsnagReporting'
import AppData from './appdata'
import DataStore from '@/api/DataStore/DataStore'
import Auth from '@/mixins/auth.js'

export default {
  name: 'AppLogging',
  data() {
    return {
      trackSuperAdmins: false,
      trackDevelopEnv : false
    }
  },
  mixins : [AppData, Auth],
  methods: {
    initRemoteLogging() {
      syncBugsnagReporting(this.APP_LOGGING)
      if (!this.isLoggingActive) return
      this.initPostHog()
      this.initClarity()
      this.initLogRocket()
      this.initBugSnag()
    },
    setRemoteLoggingUser() {
      syncBugsnagUser(this.posUser)
      if (!this.isLoggingActive) return
      this.setClarityUser()
      this.setPostHogUser()
      this.setLogRocketUser()
      this.setBugSnagUser()
      /* setTimeout(() => {
        Bugsnag.notify(new Error('Test error'))
      }, 3000) */
    },
    unsetRemoteLoggingUser() {
      syncBugsnagUser(null)
      if (!this.isLoggingActive) return
      this.unsetClarityUser()
      this.unsetPostHogUser()
      this.unsetLogRocketUser()
      this.unsetBugSnagUser()
    },

    initPostHog() {
      if (!this.isLoggingActive || !this.isPostHogActive || this.postHogInitialized) return

      Posthog.init(this.APP_LOGGING?.POSTHOG?.KEY, {
        debug                       : false, // Set this to true in development environments only using environment variables
        api_host                    : 'https://eu.i.posthog.com',
        defaults                    : '2026-05-30',
        capture_pageview            : 'history_change',
        persistence                 : 'localStorage+cookie',
        person_profiles             : 'identified_only', // or 'always' to create profiles for anonymous users as well
        autocapture                 : true,
        disable_session_recording   : false,
        enable_recording_console_log: true,
        logs                        : {
          serviceName   : 'gfp-pos',
          environment   : import.meta.env.MODE,
          serviceVersion: DataStore.app.versionWeb
        },
        loaded: (posthog) => {
          this.setPostHogUser(posthog)
        }
      })

      this.postHogInitialized = true
    },
    setPostHogUser(posthog) {
      if (!this.isLoggingActive || !this.isPostHogActive || !this.postHogInitialized) return

      if (!this.posUser) return

      const postHogObj = posthog || Posthog
      const domain = String(window.appConfig.LOCATION_DATA.SiteUrl).replace('https://', '').replace('/', '')

      postHogObj.identify(
        `${this.posUser.staff_id}@${domain}`,
        {
          StaffCustomId       : `${this.posUser.staff_id}@${domain} (${this.posUser.staff_name})`,
          IsAdmin             : this.userIsSuperAdmin,
          Permissions         : this.posUser.Permissions,
          PermissionsMap      : this.posUser.PermissionsMap,
          //Commands            : this.posUser.Commands,
          ShowSettingsWizard  : this.posUser.ShowSettingsWizard,
          SourceTypeIds       : this.posUser.SourceTypeIds,
          TermsAcceptedAt     : this.posUser.TermsAcceptedAt,
          KdsGroups           : this.posUser.KdsGroups,
          Domain              : this.posUser.Domain,
          Env                 : this.posUser.Env,
          is_guest            : this.posUser.is_guest,
          eft_pos             : this.posUser.eft_pos,
          theme_mode          : this.posUser.theme_mode,
          staff_id            : this.posUser.staff_id,
          printer_group_ids   : this.posUser.printer_group_ids,
          staff_global_id     : this.posUser.staff_global_id,
          staff_group_id      : this.posUser.staff_group_id,
          staff_firstname     : this.posUser.staff_firstname,
          staff_lastname      : this.posUser.staff_lastname,
          staff_name          : this.posUser.staff_name,
          staff_short_name    : this.posUser.staff_short_name,
          staff_email         : this.posUser.staff_email,
          staff_phone         : this.posUser.staff_phone,
          staff_locale        : this.posUser.staff_locale,
          staff_settings      : this.posUser.staff_settings,
          staff_permissions   : this.posUser.staff_permissions,
          staff_locale_catalog: this.posUser?.staff_locale_catalog || this.posUser.staff_locale,
          ws_app_key          : window.appConfig.WS.key,
          location_name       : window.appConfig.LOCATION_DATA.Name,
          location_site_url   : window.appConfig.LOCATION_DATA.SiteUrl,
          using_desktop_app   : isDesktopApp()
        }
      )
    },
    unsetPostHogUser() {
      if (!this.isLoggingActive || !this.isPostHogActive || !this.postHogInitialized) return

      Posthog.reset()
    },

    initClarity() {
      if (!this.isLoggingActive || !this.isClarityActive || this.clarityInitialized) return

      Clarity.init(this.APP_LOGGING?.CLARITY?.KEY)

      this.clarityInitialized = true
    },
    setClarityUser() {
      if (!this.isLoggingActive || !this.isClarityActive || !this.clarityInitialized) return

      if (!this.posUser) return

      // LogRocket.startNewSession()
      const domain = String(window.appConfig.LOCATION_DATA.SiteUrl).replace('https://', '').replace('/', '')

      Clarity.consent(true)
      Clarity.identify(`${this.posUser.staff_id}@${domain}`, undefined, undefined, this.posUser.staff_name)
      Clarity.setTag('domain', domain)
      Clarity.setTag('user_env', this.posUser.Env)
      Clarity.setTag('env', import.meta.env.MODE)
      Clarity.setTag('desktop', String(isDesktopApp()))
    },
    unsetClarityUser() {
      if (!this.isLoggingActive || !this.isClarityActive || !this.clarityInitialized) return

      Clarity.consent(false)
      Clarity.identify(undefined)
    },

    initLogRocket() {
      if (!this.isLoggingActive || !this.isLogRocketActive || this.logRocketInitialized) return

      LogRocket.init(this.APP_LOGGING.LOGROCKET.KEY, {
        release: DataStore.app.versionWeb,
        dom    : {
          baseHref: import.meta.env.VITE_POS_URL
        }
      })

      this.logRocketInitialized = true
    },
    setLogRocketUser() {
      if (!this.isLoggingActive || !this.isLogRocketActive || !this.logRocketInitialized) return

      if (!this.posUser) return

      // LogRocket.startNewSession()
      const domain = String(window.appConfig.LOCATION_DATA.SiteUrl).replace('https://', '').replace('/', '')
      LogRocket.identify(`${this.posUser.staff_id}@${domain}`, {
        IsAdmin             : this.userIsSuperAdmin,
        Permissions         : this.posUser.Permissions,
        PermissionsMap      : this.posUser.PermissionsMap,
        Commands            : this.posUser.Commands,
        ShowSettingsWizard  : this.posUser.ShowSettingsWizard,
        SourceTypeIds       : this.posUser.SourceTypeIds,
        TermsAcceptedAt     : this.posUser.TermsAcceptedAt,
        KdsGroups           : this.posUser.KdsGroups,
        Domain              : this.posUser.Domain,
        Env                 : this.posUser.Env,
        is_guest            : this.posUser.is_guest,
        eft_pos             : this.posUser.eft_pos,
        theme_mode          : this.posUser.theme_mode,
        staff_id            : this.posUser.staff_id,
        printer_group_ids   : this.posUser.printer_group_ids,
        staff_global_id     : this.posUser.staff_global_id,
        staff_group_id      : this.posUser.staff_group_id,
        staff_firstname     : this.posUser.staff_firstname,
        staff_lastname      : this.posUser.staff_lastname,
        staff_name          : this.posUser.staff_name,
        staff_short_name    : this.posUser.staff_short_name,
        staff_email         : this.posUser.staff_email,
        staff_phone         : this.posUser.staff_phone,
        staff_locale        : this.posUser.staff_locale,
        staff_settings      : this.posUser.staff_settings,
        staff_permissions   : this.posUser.staff_permissions,
        staff_locale_catalog: this.posUser?.staff_locale_catalog || this.posUser.staff_locale,
        ws_app_key          : window.appConfig.WS.key,
        location_name       : window.appConfig.LOCATION_DATA.Name,
        location_site_url   : window.appConfig.LOCATION_DATA.SiteUrl,
        using_desktop_app   : isDesktopApp()
      })
    },
    unsetLogRocketUser() {
      if (!this.isLoggingActive || !this.isLogRocketActive || !this.logRocketInitialized) return
      LogRocket.identify(false)
    },

    initBugSnag() {
      if (!this.isLoggingActive || !this.isBugSnagActive || this.bugSnagInitialized) return
      Bugsnag.start({
        apiKey             : this.APP_LOGGING.BUGSNAG.KEY,
        plugins            : [new BugsnagPluginVue(Vue)],
        appVersion         : DataStore.app.versionWeb,
        appType            : isDesktopApp() ? 'DESKTOP' : 'WEB',
        autoNotify         : true,
        autoCaptureSessions: true,
        collectUserIp      : true,
        releaseStage       : import.meta.env.MODE,
        notifyReleaseStages: ['production', 'staging'], // development
        filters            : [
          'token', // exact match: "token"
          'access_token', // exact match: "token"
          /^password$/i // case-insensitive: "password", "PASSWORD", "PaSsWoRd"
        ],
        redactedKeys: [
          'token', // exact match: "token"
          'access_token', // exact match: "token"
          /^password$/i // case-insensitive: "password", "PASSWORD", "PaSsWoRd"
        ]
      })
      Bugsnag.clearMetadata('app')
      Bugsnag.addMetadata('app', this.$DataStore.app || {})

      Bugsnag.beforeNotify = (data) => {
        if (this.isLogRocketActive && this.logRocketInitialized) data.metaData.logRocketSessionURL = LogRocket.sessionURL
        return data
      }

      const bugsnagVue = Bugsnag.getPlugin('vue')
      bugsnagVue.installVueErrorHandler(Vue)

      this.bugSnagInitialized = true
    },
    setBugSnagUser() {
      if (!this.isLoggingActive || !this.isBugSnagActive || !this.bugSnagInitialized) return

      this.unsetBugSnagUser()
      if (!this.posUser) return

      const domain = String(window.appConfig.LOCATION_DATA.SiteUrl).replace('https://', '').replace('/', '')
      Bugsnag.setUser(`${this.posUser.staff_id}@${domain}`, this.posUser.staff_email, this.posUser.staff_name)

      const user = JSON.parse(JSON.stringify(this.posUser))
      delete user.Commands
      delete user.Permissions

      Bugsnag.addMetadata('user', {
        ...user,
        ...{
          IsAdmin          : this.userIsSuperAdmin,
          ws_app_key       : window.appConfig.WS.key,
          location_name    : window.appConfig.LOCATION_DATA.Name,
          location_site_url: window.appConfig.LOCATION_DATA.SiteUrl,
          using_desktop_app: isDesktopApp()
        }
      })
      Bugsnag.addMetadata('commands', this.posUser.Commands)
      Bugsnag.addMetadata('permissions', this.posUser.Permissions)
    },
    unsetBugSnagUser() {
      if (!this.isLoggingActive && !this.isBugSnagActive || !this.bugSnagInitialized) return
      Bugsnag.setUser('', '', '')
      Bugsnag.clearMetadata('user')
    }
  },
  computed: {
    APP_LOGGING() {
      return this.appConfig ? this.appConfig?.APP_LOGGING : {}
    },
    isLoggingActive() {
      return Boolean(
        this.APP_LOGGING?.ENABLED &&
        (this.trackDevelopEnv || import.meta.env.MODE !== 'development') &&
        (this.trackSuperAdmins || !this.userIsSuperExtraAdmin)
      )
    },
    isLogRocketActive() {
      const apiKey = this.APP_LOGGING?.LOGROCKET?.KEY || ''
      const active = isDesktopApp() ? this.APP_LOGGING?.LOGROCKET?.DESKTOP : this.APP_LOGGING?.LOGROCKET?.WEB
      return this.isLoggingActive && active && apiKey
    },
    isClarityActive() {
      const apiKey = this.APP_LOGGING?.CLARITY?.KEY || ''
      const active = isDesktopApp() ? this.APP_LOGGING?.CLARITY?.DESKTOP : this.APP_LOGGING?.CLARITY?.WEB
      return this.isLoggingActive && active && apiKey
    },
    isPostHogActive() {
      const apiKey = this.APP_LOGGING?.POSTHOG?.KEY || ''
      const active = isDesktopApp() ? this.APP_LOGGING?.POSTHOG?.DESKTOP : this.APP_LOGGING?.POSTHOG?.WEB
      return this.isLoggingActive && active && apiKey
    },
    isBugSnagActive() {
      const apiKey = this.APP_LOGGING?.BUGSNAG?.KEY || ''
      const active = isDesktopApp() ? this.APP_LOGGING?.BUGSNAG?.DESKTOP : this.APP_LOGGING?.BUGSNAG?.WEB
      return this.isLoggingActive && active && apiKey
    },
    isAppPrintLogActive() {
      return isDesktopApp() && this.APP_LOGGING?.PRINT?.ENABLED
    },
    isAppPrintLogVisible() {
      return this.APP_LOGGING?.ENABLED && this.APP_LOGGING?.PRINT?.ENABLED && (this.APP_LOGGING?.PRINT?.VISIBLE || this.userIsSuperAdmin)
    },
    clarityInitialized: {
      set(v) {
        this.APP_LOGGING.CLARITY.INIT = v
      },
      get() {
        return this.APP_LOGGING.CLARITY.INIT
      }
    },
    postHogInitialized: {
      set(v) {
        this.APP_LOGGING.POSTHOG.INIT = v
      },
      get() {
        return this.APP_LOGGING.POSTHOG.INIT
      }
    },
    logRocketInitialized: {
      set(v) {
        this.APP_LOGGING.LOGROCKET.INIT = v
      },
      get() {
        return this.APP_LOGGING.LOGROCKET.INIT
      }
    },
    bugSnagInitialized: {
      set(v) {
        this.APP_LOGGING.BUGSNAG.INIT = v
      },
      get() {
        return this.APP_LOGGING.BUGSNAG.INIT
      }
    }
  }
}
